<ion-modal [isOpen]="isOpen" (didDismiss)="onClose()" class="card-popup">
  <ng-template>
    <ion-header>
      <ion-toolbar>
        <ion-title>Reprint labels</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="onClose()">
            <ion-icon name="close" slot="start"></ion-icon>
            Close
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <div class="reprint-body">

        <!-- Which log we're reprinting (server fills the document from this log) -->
        <div class="reprint-log-summary" *ngIf="logRow">
          <div class="reprint-summary-row">
            <span class="reprint-summary-label">PO</span>
            <span class="reprint-summary-value mono">{{ logRow.poNumber || '—' }}</span>
          </div>
          <div class="reprint-summary-row">
            <span class="reprint-summary-label">Job</span>
            <span class="reprint-summary-value mono">{{ logRow.jobNumber || '—' }}</span>
          </div>
          <div class="reprint-summary-row">
            <span class="reprint-summary-label">Vendor</span>
            <span class="reprint-summary-value">{{ logRow.vendor || '—' }}</span>
          </div>
        </div>

        <!-- What to print -->
        <div class="reprint-section">
          <div class="reprint-section-label">What to print</div>
          <ion-select class="reprint-select" [(ngModel)]="printKind" interface="popover">
            <ion-select-option value="Both">Label + copier sheet</ion-select-option>
            <ion-select-option value="Zebra">Label only</ion-select-option>
            <ion-select-option value="Copier">Copier sheet only</ion-select-option>
          </ion-select>
        </div>

        <!-- Loading the log's item codes (to decide per-item vs whole-log). -->
        <div class="reprint-section" *ngIf="itemIdsLoading">
          <div class="reprint-printer-loading">
            <ion-spinner name="crescent"></ion-spinner>
            <span>Loading items…</span>
          </div>
        </div>

        <!-- Per-item reprint: one row per physical item, each with its own
             Labels / Copier-sheets counts and reprint button (shared with the
             mobile Done screen). Shown when the log has per-item QR codes. -->
        <!-- NOTE: in this mode the footer button is "Done" — the print action is
             this list's own button, wired to onReprintAll. -->
        <!-- A count for every document this reprint actually prints, whichever path
             it takes. The browser half was left out when these rows were wired only
             to the from-log endpoint's per-item copies block — but a browser-rendered
             document is per-container too, and it reads the SAME counts (see
             _browserItems). Excluding it left a split job with a document being
             printed and no control anywhere on screen for how many. -->
        <app-item-reprint-list
          *ngIf="!itemIdsLoading && showItemReprint"
          [items]="reprintItems"
          [printsLabels]="sendsZebraToBackend || labelInBrowser"
          [printsSheets]="sendsCopierToBackend || sheetInBrowser"
          [status]="reprintStatus"
          sectionLabel="Reprint items"
          actionVerb="Reprint"
          actionVerbDone="Reprinted"
          (reprintAll)="onReprintAll()"
          (changed)="onReprintChanged()">
        </app-item-reprint-list>

        <!-- This log predates item tracking, so there are no per-item codes to
             print. Say so — a label that comes out with no QR otherwise reads as
             a fault rather than as how this log was recorded. Gated on qrExpected
             rather than on the print path: the browser path prints QR-less labels
             for this same reason and used to say nothing, while a facility that
             prints no browser QR codes at all does not need an explanation for an
             absence that is simply normal there. -->
        <div class="reprint-section reprint-no-items"
             *ngIf="!itemIdsLoading && hasNoItemRows && qrExpected">
          <ion-icon name="information-circle-outline"></ion-icon>
          <span>
            This log has no tracked items, so what it prints — labels, copier sheets, or
            both — comes out without QR codes.
            The count below is how many copies to print.
          </span>
        </div>

        <!-- Legacy whole-log quantities (log has no per-item QR codes). -->
        <div class="reprint-section" *ngIf="!itemIdsLoading && !showItemReprint">
          <div class="reprint-qty-row" *ngIf="showZebra">
            <span class="reprint-qty-label">Labels</span>
            <div class="reprint-stepper">
              <ion-button fill="clear" size="small" (click)="decLabel()" [disabled]="labelCount <= 1">
                <ion-icon slot="icon-only" name="remove-outline"></ion-icon>
              </ion-button>
              <span class="reprint-qty-count">{{ labelCount }}</span>
              <ion-button fill="clear" size="small" (click)="incLabel()">
                <ion-icon slot="icon-only" name="add-outline"></ion-icon>
              </ion-button>
            </div>
          </div>

          <div class="reprint-qty-row" *ngIf="showCopier">
            <span class="reprint-qty-label">Copier sheets</span>
            <div class="reprint-stepper">
              <ion-button fill="clear" size="small" (click)="decCopy()" [disabled]="copyCount <= 1">
                <ion-icon slot="icon-only" name="remove-outline"></ion-icon>
              </ion-button>
              <span class="reprint-qty-count">{{ copyCount }}</span>
              <ion-button fill="clear" size="small" (click)="incCopy()">
                <ion-icon slot="icon-only" name="add-outline"></ion-icon>
              </ion-button>
            </div>
          </div>
        </div>

        <!-- Where this reprint goes, PER DOCUMENT. One section, because a job can
             split: with the copier marked down the sheet opens in a print dialog
             while the label still goes to the Zebra through the print API, and the
             operator needs both halves of that sentence.

             Every note below is chosen by which DOCUMENT is on which path — not by
             the whole-job `usesBrowserPrint`, which is what made this section say
             "the Zebra is marked as down" to someone who had marked the copier
             down and asked for a copier sheet. -->
        <div class="reprint-section" *ngIf="labelInBrowser || sheetInBrowser
                                            || sendsZebraToBackend || sendsCopierToBackend">
          <div class="reprint-section-label">Printer</div>

          <!-- "your browser's print dialog", not "this computer's": this modal is
               opened from the log table, which a phone can reach too, and a phone
               has no printer behind that dialog. Naming the browser is true on
               both; naming a computer would promise paper to one of them. -->
          <div class="reprint-printer-note" *ngIf="labelInBrowser">
            <ion-icon name="print-outline"></ion-icon>
            <!-- Two reasons land here and they are not the same news: no printer
                 configured at all, versus one this device has been told is down.
                 The second is temporary and the operator set it themselves. -->
            <span *ngIf="labelDivertedByOutage">The Zebra is marked as down on this device, so labels open in your
              browser's print dialog — choose the printer there.</span>
            <span *ngIf="!labelDivertedByOutage">This location has no Zebra label printer set up, so labels open in
              your browser's print dialog — choose the printer there.</span>
          </div>

          <div class="reprint-printer-note" *ngIf="sheetInBrowser">
            <ion-icon name="print-outline"></ion-icon>
            <!-- The copier's difference is a WIDER choice, not a degraded one: the
                 print dialog reaches any copier this computer can, where the print
                 API only ever reaches the one configured for the location. -->
            <span *ngIf="sheetDivertedByOutage">The copier is marked as down on this device, so copier sheets open in
              your browser's print dialog — choose any copier there.</span>
            <span *ngIf="!sheetDivertedByOutage">This location's copier isn't set up for automatic printing, so copier
              sheets open in your browser's print dialog — choose any copier there.</span>
          </div>

          <!-- Browsers scale a page to fit by default, and a scaled document prints
               a QR too small to scan. The one setting that matters, said on the
               screen immediately before the dialog opens. Asked of the documents
               actually rendering here — a facility can carry QR on its copier
               sheets and not on its Dymo labels. -->
          <div class="reprint-printer-note" *ngIf="browserDocsCarryQr">
            <ion-icon name="resize-outline"></ion-icon>
            <span><strong>Set Scale to "Actual size" (100%) in that dialog</strong> — anything
              smaller shrinks the QR code until it will not scan.</span>
          </div>

          <div *ngIf="printersLoading" class="reprint-printer-loading">
            <ion-spinner name="crescent"></ion-spinner>
            <span>Loading printers…</span>
          </div>

          <!-- Only for documents actually going to the print API — see
               sendsZebraToBackend. -->
          <div class="reprint-printer-row" *ngIf="sendsZebraToBackend">
            <span class="reprint-printer-label">Label printer</span>
            <ion-select class="reprint-select" [(ngModel)]="selectedZebraPrinter"
                        interface="popover" placeholder="Choose printer">
              <ion-select-option *ngIf="selectedZebraPrinter && !printerNames.includes(selectedZebraPrinter)"
                                 [value]="selectedZebraPrinter">
                {{ selectedZebraPrinter }} · configured
              </ion-select-option>
              <ion-select-option *ngFor="let p of availablePrinters" [value]="p.name">
                {{ p.name }}{{ p.isOnline ? '' : ' · offline' }}
              </ion-select-option>
            </ion-select>
          </div>

          <div class="reprint-printer-row" *ngIf="sendsCopierToBackend">
            <span class="reprint-printer-label">Copier printer</span>
            <ion-select class="reprint-select" [(ngModel)]="selectedCopierPrinter"
                        interface="popover" placeholder="Choose printer">
              <ion-select-option *ngIf="selectedCopierPrinter && !printerNames.includes(selectedCopierPrinter)"
                                 [value]="selectedCopierPrinter">
                {{ selectedCopierPrinter }} · configured
              </ion-select-option>
              <ion-select-option *ngFor="let p of availablePrinters" [value]="p.name">
                {{ p.name }}{{ p.isOnline ? '' : ' · offline' }}
              </ion-select-option>
            </ion-select>
          </div>
        </div>

        <!-- Deferred: a phone under a printer outage. The backend is ruled out and
             the browser dialog on a device in someone's hand reaches no printer,
             so this says what cannot print and where it can, rather than opening a
             dialog that does nothing. Danger styling only when NOTHING can print
             (Confirm is disabled then, like the two guards below); a split job
             still has a printable half, so it reads as a note beside it. -->
        <div *ngIf="hasDeferredPrinting"
             [class.reprint-warning]="allDeferred"
             [class.reprint-printer-note]="!allDeferred">
          <ion-icon [name]="allDeferred ? 'alert-circle-outline' : 'phone-portrait-outline'"></ion-icon>
          <span>{{ deferredMessage }}</span>
        </div>

        <!-- Guard messages: the endpoint 400s without the number a document needs -->
        <div class="reprint-warning" *ngIf="missingJobNumber">
          <ion-icon name="alert-circle-outline"></ion-icon>
          This log has no job number, so the label can't be reprinted.
        </div>
        <div class="reprint-warning" *ngIf="missingPoNumber">
          <ion-icon name="alert-circle-outline"></ion-icon>
          This log has no PO number, so the copier sheet can't be reprinted.
        </div>
      </div>
    </ion-content>

    <ion-footer>
      <ion-toolbar>
        <ion-buttons slot="end">
          <!-- Per-item mode prints from each row's button; the footer just closes. -->
          <ion-button *ngIf="showItemReprint" (click)="onClose()" class="submit-btn">
            <ion-icon name="checkmark" slot="start"></ion-icon>
            &nbsp;Done
          </ion-button>
          <!-- Legacy whole-log mode: one Send action. -->
          <ion-button *ngIf="!showItemReprint" (click)="onConfirm()" [disabled]="!canSubmit" class="submit-btn">
            <ion-spinner *ngIf="isSending || itemIdsLoading" name="crescent"></ion-spinner>
            <ion-icon *ngIf="!isSending && !itemIdsLoading" name="print" slot="start"></ion-icon>
            <!-- "Send to printer" describes queueing a job at a named printer; the
                 browser path opens a local print dialog instead. -->
            &nbsp;{{ isSending ? 'Sending…' : (itemIdsLoading ? 'Loading…' : (usesBrowserPrint ? 'Print' : 'Send to printer')) }}
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-footer>
  </ng-template>
</ion-modal>
